

import React from 'react';
import { FileText, TrendingUp, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatCurrency } from '@/lib/data';

interface Quotation {
  id: number;
  quotation_number: string;
  client?: { name: string } | null;
  name?: string;
  total_amount: number;
  status: string;
}

interface QuotationsWidgetProps {
  quotations: Quotation[];
  loading?: boolean;
}

const statusColors = {
  draft: 'bg-stone-100 text-stone-700',
  sent: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  expired: 'bg-orange-100 text-orange-700',
};

export function QuotationsWidget({ quotations, loading = false }: QuotationsWidgetProps) {
  const displayQuotations = quotations.slice(0, 5);

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-stone-800">Recent Quotations</h3>
        <Link to="/quotations" className="text-xs text-amber-600 hover:text-amber-700 font-medium">
          View all
        </Link>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-20 bg-stone-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : displayQuotations.length === 0 ? (
        <p className="text-sm text-stone-400 text-center py-8">No quotations yet</p>
      ) : (
        <div className="space-y-3">
          {displayQuotations.map((quotation) => (
            <Link
              key={quotation.id}
              to={`/quotations/${quotation.id}`}
              className="block p-3 rounded-lg border border-stone-100 hover:border-stone-300 hover:bg-stone-50 transition-all group"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="w-4 h-4 text-stone-400 flex-shrink-0" />
                    <p className="font-medium text-sm truncate group-hover:text-amber-600">
                      {quotation.quotation_number}
                    </p>
                  </div>

                  <p className="text-xs text-stone-500 truncate mb-2">
                    {quotation.client?.name || quotation.name || 'N/A'}
                  </p>

                  <div className="flex items-center gap-2 text-xs text-stone-500">
                    <TrendingUp className="w-3 h-3" />
                    <span className="font-semibold text-stone-800">
                      {formatCurrency(quotation.total_amount)}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${
                    statusColors[quotation.status as keyof typeof statusColors] || 'bg-stone-100 text-stone-700'
                  }`}>
                    {quotation.status}
                  </span>
                  <ChevronRight className="w-4 h-4 text-stone-400 group-hover:text-stone-600" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
